import { FEATURES } from '../../app/terminology/features';
import {
    Badge,
    Banner,
    BlockStack,
    Box,
    Button,
    ButtonGroup,
    Card,
    EmptyState,
    IndexTable,
    InlineStack,
    Layout,
    Modal,
    Page,
    Select,
    SkeletonBodyText,
    SkeletonPage,
    Tabs,
    Text,
    TextField,
} from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import {
    ServiceBookingAppointment,
    ServiceBookingAppointmentList,
    ServiceBookingCalendar,
    ServiceBookingStatus,
} from '../../app/models/ServiceBooking';

const dateKey = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const offsetDate = (value: string, days: number) => {
    const [year, month, day] = value.split('-').map(Number);
    const date = new Date(year, month - 1, day, 12);
    date.setDate(date.getDate() + days);
    return dateKey(date);
};

const formatSelectedDate = (value: string) => {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day, 12).toLocaleDateString(undefined, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
};

const formatAppointmentDate = (value: string, timeZone?: string) =>
    new Date(value).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        ...(timeZone ? { timeZone } : {}),
    });

const formatTime = (value: string, timeZone?: string) =>
    new Date(value).toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
        ...(timeZone ? { timeZone } : {}),
    });

const currency = (value: number) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);

const statusLabel = (status: ServiceBookingStatus) => {
    if (status === 'PendingPayment') return 'Pending payment';
    if (status === 'NoShow') return 'No-show';
    return status;
};

const badgeTone = (status: ServiceBookingStatus): 'attention' | 'success' | 'critical' | 'info' | undefined => {
    if (status === 'Confirmed' || status === 'Completed') return 'success';
    if (status === 'Cancelled' || status === 'NoShow') return 'critical';
    if (status === 'PendingPayment') return 'attention';
    return 'info';
};

const statusOptions: Array<{ label: string; value: ServiceBookingStatus }> = [
    { label: 'Pending payment', value: 'PendingPayment' },
    { label: 'Confirmed', value: 'Confirmed' },
    { label: 'Completed', value: 'Completed' },
    { label: 'Cancelled', value: 'Cancelled' },
    { label: 'No-show', value: 'NoShow' },
];

const filterStatusOptions = [
    { label: 'All statuses', value: '' },
    ...statusOptions,
];

const ServiceBookingCalendarPage = () => {
    const navigate = useNavigate();
    const initialPaymentSyncStarted = useRef(false);
    const [selectedView, setSelectedView] = useState(0);
    const [selectedDate, setSelectedDate] = useState(() => dateKey(new Date()));
    const [appointmentList, setAppointmentList] = useState<ServiceBookingAppointmentList>();
    const [calendar, setCalendar] = useState<ServiceBookingCalendar>();
    const [loadingOverview, setLoadingOverview] = useState(true);
    const [loadingCalendar, setLoadingCalendar] = useState(true);
    const [syncingPayments, setSyncingPayments] = useState(false);
    const [error, setError] = useState('');
    const [syncError, setSyncError] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [selectedAppointment, setSelectedAppointment] = useState<ServiceBookingAppointment>();
    const [nextStatus, setNextStatus] = useState<ServiceBookingStatus>('PendingPayment');
    const [savingStatus, setSavingStatus] = useState(false);

    const loadOverview = useCallback(async () => {
        setLoadingOverview(true);
        setError('');
        try {
            setAppointmentList(await agent.ServiceBookings.appointments());
        } catch (requestError: any) {
            setError(requestError?.response?.data?.detail || requestError?.message || 'Appointments could not be loaded.');
        } finally {
            setLoadingOverview(false);
        }
    }, []);

    const loadCalendar = useCallback(async (date: string) => {
        setLoadingCalendar(true);
        setError('');
        try {
            setCalendar(await agent.ServiceBookings.calendar(date));
        } catch (requestError: any) {
            setError(requestError?.response?.data?.detail || requestError?.message || 'Appointments could not be loaded.');
        } finally {
            setLoadingCalendar(false);
        }
    }, []);

    const syncPayments = useCallback(async (silent = false) => {
        setSyncingPayments(true);
        setSyncError('');
        try {
            await agent.ServiceBookings.reconcilePayments();
            await Promise.all([loadOverview(), loadCalendar(selectedDate)]);
        } catch (requestError: any) {
            if (!silent) {
                setSyncError(requestError?.response?.data?.detail || requestError?.message || 'Shopify payments could not be synchronized.');
            }
            await loadOverview();
        } finally {
            setSyncingPayments(false);
        }
    }, [loadCalendar, loadOverview, selectedDate]);

    useEffect(() => {
        if (initialPaymentSyncStarted.current) return;
        initialPaymentSyncStarted.current = true;
        syncPayments(true);
    }, [syncPayments]);

    useEffect(() => {
        loadCalendar(selectedDate);
    }, [loadCalendar, selectedDate]);

    const allAppointments = useMemo(() => appointmentList?.appointments ?? [], [appointmentList?.appointments]);
    const filteredAppointments = useMemo(() => {
        const normalizedSearch = search.trim().toLowerCase();
        return allAppointments.filter((appointment) => {
            if (statusFilter && appointment.status !== statusFilter) return false;
            if (!normalizedSearch) return true;
            return [
                appointment.customerName,
                appointment.customerEmail,
                appointment.customerPhone,
                appointment.vehicleLabel,
                appointment.serviceName,
                appointment.productSku,
                appointment.shopifyOrderName,
            ].some((value) => value?.toLowerCase().includes(normalizedSearch));
        });
    }, [allAppointments, search, statusFilter]);
    const dayAppointments = useMemo(() => calendar?.appointments ?? [], [calendar?.appointments]);
    const summary = useMemo(() => ({
        total: allAppointments.length,
        confirmed: allAppointments.filter((appointment) => appointment.status === 'Confirmed').length,
        pending: allAppointments.filter((appointment) => appointment.status === 'PendingPayment').length,
    }), [allAppointments]);

    const openAppointment = (appointment: ServiceBookingAppointment) => {
        setSelectedAppointment(appointment);
        setNextStatus(appointment.status);
    };

    const replaceAppointment = (updated: ServiceBookingAppointment) => {
        setAppointmentList((current) => current ? {
            ...current,
            appointments: current.appointments.map((appointment) =>
                appointment.id === updated.id ? updated : appointment),
        } : current);
        setCalendar((current) => current ? {
            ...current,
            appointments: current.appointments.map((appointment) =>
                appointment.id === updated.id ? updated : appointment),
        } : current);
    };

    const saveStatus = async () => {
        if (!selectedAppointment) return;
        setSavingStatus(true);
        try {
            const updated = await agent.ServiceBookings.updateStatus(selectedAppointment.id, nextStatus);
            replaceAppointment(updated);
            setSelectedAppointment(updated);
        } finally {
            setSavingStatus(false);
        }
    };

    if (loadingOverview && !appointmentList) {
        return (
            <SkeletonPage title={FEATURES.serviceBooking}>
                <Layout>
                    <Layout.Section><Card><SkeletonBodyText lines={8} /></Card></Layout.Section>
                </Layout>
            </SkeletonPage>
        );
    }

    const timeZoneId = selectedView === 0 ? appointmentList?.timeZoneId : calendar?.timeZoneId;

    return (
        <Page
            title={FEATURES.serviceBooking}
            subtitle={appointmentList?.locationName || calendar?.locationName || 'Appointments'}
            primaryAction={{ content: 'Booking settings', onAction: () => navigate('/service-booking/settings') }}
            secondaryActions={[{
                content: 'Sync Shopify payments',
                onAction: () => syncPayments(false),
                loading: syncingPayments,
            }]}
        >
            <Layout>
                <Layout.Section>
                    <BlockStack gap="400">
                        <Banner title="Proof of concept" tone="info">
                            <p>This booking view currently represents one location.</p>
                        </Banner>
                        {error && <Banner title="Appointments could not be loaded" tone="critical"><p>{error}</p></Banner>}
                        {syncError && <Banner title="Shopify payments could not be synchronized" tone="warning"><p>{syncError}</p></Banner>}
                        <Card padding="0">
                            <Tabs
                                tabs={[
                                    { id: 'appointments', content: 'All appointments' },
                                    { id: 'day', content: 'Day view' },
                                ]}
                                selected={selectedView}
                                onSelect={setSelectedView}
                            />
                        </Card>

                        {selectedView === 0 ? (
                            <>
                                <Card>
                                    <BlockStack gap="400">
                                        <InlineStack gap="600" wrap>
                                            <Summary label="Appointments" value={summary.total} />
                                            <Summary label="Confirmed" value={summary.confirmed} />
                                            <Summary label="Pending payment" value={summary.pending} />
                                        </InlineStack>
                                        <InlineStack gap="300" blockAlign="end" wrap>
                                            <Box minWidth="280px">
                                                <TextField
                                                    label="Search appointments"
                                                    value={search}
                                                    onChange={setSearch}
                                                    placeholder="Customer, machine, service, order"
                                                    autoComplete="off"
                                                    clearButton
                                                    onClearButtonClick={() => setSearch('')}
                                                />
                                            </Box>
                                            <Box minWidth="200px">
                                                <Select
                                                    label="Status"
                                                    options={filterStatusOptions}
                                                    value={statusFilter}
                                                    onChange={setStatusFilter}
                                                />
                                            </Box>
                                        </InlineStack>
                                    </BlockStack>
                                </Card>
                                <AppointmentTable
                                    appointments={filteredAppointments}
                                    timeZoneId={timeZoneId}
                                    includeDate
                                    loading={loadingOverview}
                                    emptyHeading="No appointments found"
                                    emptyMessage="Adjust the filters or complete a storefront booking."
                                    onOpen={openAppointment}
                                />
                            </>
                        ) : (
                            <>
                                <Card>
                                    <BlockStack gap="400">
                                        <InlineStack align="space-between" blockAlign="center" gap="300">
                                            <BlockStack gap="050">
                                                <Text as="h2" variant="headingMd">{formatSelectedDate(selectedDate)}</Text>
                                                <Text as="p" tone="subdued">{calendar?.timeZoneId || 'Location time zone'}</Text>
                                            </BlockStack>
                                            <ButtonGroup>
                                                <Button onClick={() => setSelectedDate(offsetDate(selectedDate, -1))}>Previous day</Button>
                                                <Button onClick={() => setSelectedDate(dateKey(new Date()))}>Today</Button>
                                                <Button onClick={() => setSelectedDate(offsetDate(selectedDate, 1))}>Next day</Button>
                                            </ButtonGroup>
                                        </InlineStack>
                                    </BlockStack>
                                </Card>
                                <AppointmentTable
                                    appointments={dayAppointments}
                                    timeZoneId={timeZoneId}
                                    loading={loadingCalendar}
                                    emptyHeading="No appointments on this date"
                                    emptyMessage="Choose another day or complete a storefront booking."
                                    onOpen={openAppointment}
                                />
                            </>
                        )}
                    </BlockStack>
                </Layout.Section>
            </Layout>

            <Modal
                open={!!selectedAppointment}
                onClose={() => setSelectedAppointment(undefined)}
                title="Appointment details"
                primaryAction={{ content: 'Update status', onAction: saveStatus, loading: savingStatus }}
                secondaryActions={[{ content: 'Close', onAction: () => setSelectedAppointment(undefined) }]}
            >
                <Modal.Section>
                    {selectedAppointment && (
                        <BlockStack gap="500">
                            <Select
                                label="Appointment status"
                                options={statusOptions}
                                value={nextStatus}
                                onChange={(value) => setNextStatus(value as ServiceBookingStatus)}
                            />
                            <BlockStack gap="300">
                                <Detail label="Date" value={formatAppointmentDate(selectedAppointment.startsAtUtc, timeZoneId)} />
                                <Detail label="Time" value={`${formatTime(selectedAppointment.startsAtUtc, timeZoneId)} - ${formatTime(selectedAppointment.endsAtUtc, timeZoneId)}`} />
                                <Detail label="Service" value={selectedAppointment.serviceName} />
                                <Detail label="Machine" value={selectedAppointment.vehicleLabel} />
                                <Detail label="Customer" value={`${selectedAppointment.customerName} | ${selectedAppointment.customerPhone} | ${selectedAppointment.customerEmail}`} />
                                <Detail label="Pricing" value={`${currency(selectedAppointment.laborPrice)} labor | ${currency(selectedAppointment.depositAmount)} deposit`} />
                                {selectedAppointment.shopifyOrderName && <Detail label="Shopify order" value={selectedAppointment.shopifyOrderName} />}
                                {selectedAppointment.productTitle && <Detail label="Product" value={`${selectedAppointment.productTitle}${selectedAppointment.productSku ? ` | ${selectedAppointment.productSku}` : ''}`} />}
                                {selectedAppointment.customerNotes && <Detail label="Notes" value={selectedAppointment.customerNotes} />}
                                <Detail label="Reference" value={selectedAppointment.publicId} />
                            </BlockStack>
                        </BlockStack>
                    )}
                </Modal.Section>
            </Modal>
        </Page>
    );
};

const AppointmentTable = ({
    appointments,
    timeZoneId,
    includeDate = false,
    loading,
    emptyHeading,
    emptyMessage,
    onOpen,
}: {
    appointments: ServiceBookingAppointment[];
    timeZoneId?: string;
    includeDate?: boolean;
    loading: boolean;
    emptyHeading: string;
    emptyMessage: string;
    onOpen: (appointment: ServiceBookingAppointment) => void;
}) => (
    <Card padding="0">
        {appointments.length === 0 ? (
            <Box padding="600">
                <EmptyState heading={emptyHeading} image="">
                    <p>{emptyMessage}</p>
                </EmptyState>
            </Box>
        ) : (
            <IndexTable
                resourceName={{ singular: 'appointment', plural: 'appointments' }}
                itemCount={appointments.length}
                selectable={false}
                headings={[
                    { title: includeDate ? 'Date / time' : 'Time' },
                    { title: 'Service' },
                    { title: 'Customer' },
                    { title: 'Machine / product' },
                    { title: 'Status' },
                    { title: '' },
                ]}
                loading={loading}
            >
                {appointments.map((appointment, index) => (
                    <IndexTable.Row id={String(appointment.id)} key={appointment.id} position={index}>
                        <IndexTable.Cell>
                            <BlockStack gap="050">
                                {includeDate && <Text as="span" variant="bodySm" tone="subdued">{formatAppointmentDate(appointment.startsAtUtc, timeZoneId)}</Text>}
                                <Text as="span" variant="bodyMd" fontWeight="semibold">{formatTime(appointment.startsAtUtc, timeZoneId)}</Text>
                                <Text as="span" variant="bodySm" tone="subdued">to {formatTime(appointment.endsAtUtc, timeZoneId)}</Text>
                            </BlockStack>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <BlockStack gap="050">
                                <Text as="span" fontWeight="semibold">{appointment.serviceName}</Text>
                                <Text as="span" variant="bodySm" tone="subdued">{currency(appointment.laborPrice)} labor</Text>
                            </BlockStack>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <BlockStack gap="050">
                                <Text as="span">{appointment.customerName}</Text>
                                <Text as="span" variant="bodySm" tone="subdued">{appointment.customerPhone}</Text>
                            </BlockStack>
                        </IndexTable.Cell>
                        <IndexTable.Cell>
                            <BlockStack gap="050">
                                <Text as="span">{appointment.vehicleLabel}</Text>
                                {appointment.productSku && <Text as="span" variant="bodySm" tone="subdued">SKU {appointment.productSku}</Text>}
                            </BlockStack>
                        </IndexTable.Cell>
                        <IndexTable.Cell><Badge tone={badgeTone(appointment.status)}>{statusLabel(appointment.status)}</Badge></IndexTable.Cell>
                        <IndexTable.Cell>
                            <InlineStack align="end"><Button onClick={() => onOpen(appointment)}>View</Button></InlineStack>
                        </IndexTable.Cell>
                    </IndexTable.Row>
                ))}
            </IndexTable>
        )}
    </Card>
);

const Summary = ({ label, value }: { label: string; value: number }) => (
    <BlockStack gap="050">
        <Text as="span" tone="subdued">{label}</Text>
        <Text as="strong" variant="headingLg">{value}</Text>
    </BlockStack>
);

const Detail = ({ label, value }: { label: string; value: string }) => (
    <BlockStack gap="050">
        <Text as="span" variant="bodySm" tone="subdued">{label}</Text>
        <Text as="span">{value}</Text>
    </BlockStack>
);

export default ServiceBookingCalendarPage;
